import { CommonModule } from '@angular/common';
import { Component, DOCUMENT, Inject, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JobOfferService } from 'src/app/api/services';
import { JobOfferPostulantService } from 'src/app/api/services/job-offer-postulant.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowPath,
  heroArrowRight,
  heroBriefcase,
  heroCalendar,
  heroChartPie,
  heroEye,
  heroUser,
  heroUserCircle,
  heroUsers,
} from '@ng-icons/heroicons/outline';
import { RelativeDatePipe } from 'src/app/shared/pipe/relative-date.pipe';
import {
  bootstrapGenderFemale,
  bootstrapGenderMale,
} from '@ng-icons/bootstrap-icons';
import { ToastService } from 'src/app/shared/service/toast.service';
import { MESSAGES } from 'src/app/core/constant/messages';
import { JobOffer, JobOffersStats } from 'src/app/shared/model/job-detail';
import {
  ionBriefcaseOutline,
  ionLocationOutline,
  ionPeopleOutline,
  ionTimerOutline,
} from '@ng-icons/ionicons';
import { finalize, forkJoin } from 'rxjs';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { StatusProcess } from 'src/app/core/enum/status-process';
import { NavButtonComponent } from 'src/app/shared/component/nav-button/nav-button.component';
import { PercentagePipe } from 'src/app/shared/pipe/percentage.pipe';
import { BaseFormComponent } from 'src/app/shared/form/base-form.component';
import { JobOfferUpdateComponent } from './components/job-offer-update/job-offer-update.component';
import { LoadingComponent } from 'src/app/shared/component/loading/loading.component';
import { handleApiError } from 'src/app/shared/util/handle-api-error';
import { CleanPercentPipe } from 'src/app/shared/pipe/clean-percent.pipe';
import { UpdateJobOfferData } from '@models/jobOffer-payload-model';

@Component({
  selector: 'app-job-offers-details',
  imports: [
    CommonModule,
    NgIcon,
    RelativeDatePipe,
    ReactiveFormsModule,
    FormsModule,
    LoadingButtonComponent,
    NavButtonComponent,
    CleanPercentPipe,
    JobOfferUpdateComponent,
    LoadingComponent,
  ],
  templateUrl: './job-offers-details.component.html',
  viewProviders: [
    provideIcons({
      heroUsers,
      heroUserCircle,
      heroEye,
      heroChartPie,
      heroBriefcase,
      heroCalendar,
      bootstrapGenderFemale,
      bootstrapGenderMale,
      ionBriefcaseOutline,
      ionPeopleOutline,
      ionLocationOutline,
      ionTimerOutline,
      heroArrowPath,
      heroUser,
    }),
  ],
})
export class JobOffersDetailsComponent
  extends BaseFormComponent
  implements OnInit
{
  jobOffer!: JobOffer;
  actionType:
    | 'revisar'
    | 'preseleccionar'
    | 'seleccionar'
    | 'retroceder'
    | null = null;
  selectedUserId: string | null = null;
  confirmModalOpen: boolean = false;
  isLoadingStatusProcess: boolean = false;
  stats!: JobOffersStats;

  actionMessages: Record<Exclude<typeof this.actionType, null>, string> = {
    revisar: 'marcar al postulante como revisado',
    preseleccionar: 'marcar al postulante como preseleccionado',
    seleccionar: 'marcar al postulante como seleccionado',
    retroceder: 'devolver al postulante a la etapa anterior',
  };

  postulants: any[] = [];
  jobOfferId: string = '';

  loading: boolean = true;
  loadingSubmit: boolean = false;
  editModal: boolean = false;
  loadingUpdate: boolean = false;
  closeOfferModalOpen: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private jobOfferPostulantService: JobOfferPostulantService,
    private jobOfferService: JobOfferService,
    private fb: FormBuilder,
    toastService: ToastService,
  ) {
    super(toastService);
  }

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    this.route.params.subscribe((params) => {
      this.jobOfferId = params['id'];
      if (this.jobOfferId) {
        this.loadAllData();
      }
    });
  }

  loadAllData() {
    this.loading = true;

    const jobOffer$ = this.jobOfferService.ofertaEmpleoIdGet({
      idJobOffer: this.jobOfferId,
    });
    const postulants$ = this.jobOfferPostulantService.getPostulantsByIdJobOffer(
      this.jobOfferId
    );
    const stats$ = this.jobOfferService.getJobOfferDashboardData(
      this.jobOfferId
    );

    forkJoin([jobOffer$, postulants$, stats$]).subscribe({
      next: ([jobOfferRes, postulantsRes, statsRes]) => {
        this.jobOffer = jobOfferRes.dto.jobOffer;
        this.postulants = postulantsRes.dto.jobOfferPostulants;
        this.stats = statsRes.dto.jobOffers;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }

  loadStats(): void {
    this.jobOfferService
      .getJobOfferDashboardData(this.jobOfferId)
      .pipe(handleApiError(this.toastService, MESSAGES.COMPANY.STASTS_ERROR))
      .subscribe((res: any) => {
        this.stats = res?.dto?.jobOffers;
      });
  }

  loadPostulants(): void {
    this.jobOfferPostulantService
      .getPostulantsByIdJobOffer(this.jobOfferId)
      .pipe(handleApiError(this.toastService, MESSAGES.POSTULANT.GET_ALL_ERROR))
      .subscribe((res: any) => {
        this.postulants = res.dto.jobOfferPostulants;
      });
  }

  loadJobOffer(): void {
    this.jobOfferService
      .ofertaEmpleoIdGet({ idJobOffer: this.jobOfferId })
      .pipe(handleApiError(this.toastService, MESSAGES.JOB_OFFER.GET_ERROR))
      .subscribe((res: any) => {
        this.jobOffer = res.dto.jobOffer;
      });
  }

  openCloseOfferConfirm() {
    this.closeOfferModalOpen = true;
  }

  closeCloseOfferModal() {
    this.closeOfferModalOpen = false;
  }

  confirmCloseOffer(): void {
    this.loadingSubmit = true;

    this.jobOfferService
      .finalizeJobOffer(this.jobOfferId)
      .pipe(
        handleApiError(this.toastService, MESSAGES.JOB_OFFER.CLOSE_ERROR),
        finalize(() => (this.loadingSubmit = false))
      )
      .subscribe(() => {
        this.closeOfferModalOpen = false;
        this.toastService.showMessages('success', [
          MESSAGES.JOB_OFFER.CLOSE_SUCCESS,
        ]);
        this.jobOffer = { ...this.jobOffer, publicationStatus: 'Finalizado' };
      });
  }

  openConfirmModal(
    action: 'revisar' | 'preseleccionar' | 'seleccionar' | 'retroceder',
    userId: string
  ) {
    this.actionType = action;
    this.selectedUserId = userId;
    this.confirmModalOpen = true;
  }

  closeConfirmModal() {
    this.confirmModalOpen = false;
    this.selectedUserId = null;
  }

  confirmAction() {
    if (!this.selectedUserId || !this.actionType) return;

    let status: StatusProcess;

    switch (this.actionType) {
      case 'revisar':
        status = StatusProcess.Revisado;
        break;
      case 'preseleccionar':
        status = StatusProcess.Preseleccionado;
        break;
      case 'seleccionar':
        status = StatusProcess.Seleccionado;
        break;
      case 'retroceder':
        const user = this.postulants.find(
          (u) => u.idJobOfferPostulant === this.selectedUserId
        );
        if (!user) return;

        switch (user.statusProcess) {
          case StatusProcess.Seleccionado:
            status = StatusProcess.Preseleccionado;
            break;
          case StatusProcess.Preseleccionado:
            status = StatusProcess.Revisado;
            break;
          default:
            return;
        }
        break;
      default:
        return;
    }

    this.isLoadingStatusProcess = true;

    this.jobOfferPostulantService
      .updateStatusProcessByIdJobOfferPostulant(this.selectedUserId, status)
      .pipe(
        handleApiError(
          this.toastService,
          MESSAGES.POSTULANT.UPDATE_POSTULANT_JOB_ERROR
        ),
        finalize(() => (this.isLoadingStatusProcess = false))
      )
      .subscribe(() => {
        this.toastService.showMessages('success', [
          `Postulante marcado como ${status}`,
        ]);

        const user = this.postulants.find(
          (u) => u.idJobOfferPostulant === this.selectedUserId
        );
        if (user) {
          user.statusProcess = status;

          user.statusProcessRevisadoDisabled =
            status !== StatusProcess.Revisado;
          user.statusProcessPreseleccionadoDisabled =
            status === StatusProcess.Seleccionado;
        }

        this.closeConfirmModal();
      });
  }

  viewUserProfile(userId: number) {
    this.router.navigate(['../../view', userId], { relativeTo: this.route });
  }

  markAsReviewed(postulantId: string): void {
    this.jobOfferPostulantService
      .updateRevisedByIdJobOfferPostulant(postulantId)
      .subscribe({
        next: () => {
          this.loadPostulants();
          this.closeConfirmModal();
          this.toastService.showMessages('success', [
            'Postulante revisado correctamente',
          ]);
        },
        error: (err) => {
          this.toastService.showMessages('error', [
            'No se pudo revisar al postulante',
          ]);
        },
      });
  }

  markAsSelected(postulantId: string): void {
    this.jobOfferPostulantService
      .updateSelectedByIdJobOfferPostulant(postulantId)
      .subscribe({
        next: () => {
          this.loadPostulants();
          this.closeConfirmModal();
          this.toastService.showMessages('success', [
            'Postulante seleccionado correctamente',
          ]);
        },
        error: (err) => {
          this.toastService.showMessages('error', [
            'No se pudo seleccionar al postulante',
          ]);
        },
      });
  }

  enableEdit() {
    this.editModal = true;
  }

  cancelEdit() {
    this.editModal = false;
  }

  updateJobOffer(data: UpdateJobOfferData) {
    this.loadingUpdate = true;
    this.jobOfferService
      .updateJobOffer(this.jobOffer.idJobOffer, data)
      .subscribe({
        next: () => {
          this.loadJobOffer();
          this.toastService.showMessages('success', [
            MESSAGES.JOB_OFFER.UPDATE_SUCCESS,
          ]);

          this.loadingUpdate = false;
          this.editModal = false;
        },
        error: (err) => {
          const mensajes = err?.error?.listMessage?.length
            ? err.error.listMessage
            : [MESSAGES.JOB_OFFER.UPDATE_ERROR];
          this.toastService.showMessages('error', mensajes);
          this.loadingUpdate = false;
        },
      });
  }

  get StatusOffer(): boolean {
    return this.jobOffer
      ? this.jobOffer.publicationStatus === 'Publicado'
      : false;
  }

  getNextAction(user: any): 'revisar' | 'preseleccionar' | 'seleccionar' {
    switch (user.statusProcess) {
      case 'Enviado':
        return 'revisar';
      case 'Revisado':
        return 'preseleccionar';
      case 'Preseleccionado':
        return 'seleccionar';
      default:
        return 'seleccionar';
    }
  }

  getNextActionLabel(user: any): string {
    switch (user.statusProcess) {
      case 'Enviado':
        return 'Marcar como revisado';
      case 'Revisado':
        return 'Marcar como preseleccionado';
      case 'Preseleccionado':
        return 'Marcar como seleccionado';
      case 'Seleccionado':
        return 'Usuario seleccionado';
      default:
        return 'Marcar';
    }
  }

  getStatusLabel(user: any): string {
    switch (user.statusProcess) {
      case 'Enviado':
        return 'Nueva postulación';
      case 'Revisado':
        return 'Perfil revisado';
      case 'Preseleccionado':
        return 'Preseleccionado';
      case 'Seleccionado':
        return 'Seleccionado';
      default:
        return 'Desconocido';
    }
  }

  get routeNav() {
    return `/user/company/${this.jobOffer.company.idCompany}/job-offers`;
  }

  get actionMessage(): string {
    if (!this.actionType) return '';
    return this.actionMessages[this.actionType];
  }
}
